// ============================================
// Curated Nurse Personas — Seed Data
// 8 pre-built Fashion Plate configurations
// ============================================

import type { NursePersona, GarmentState } from '../../types/fashionPlate';
import type { ContextLayer } from '../../types/patientJourney';
import { defaultGarments, defaultContext } from './defaultFashionPlate';

// --- 0. The Hemologix Nurse (Dr. C Default) ---
// The baseline. The center of the dial. The nurse who greets Marianna.
// Garments and context are defined in defaultFashionPlate.ts (single source of truth).

// --- 1. The Night Nurse ---

const nightNurseGarments: GarmentState[] = [
  {
    piece: 'shoes',
    label: 'Soft slippers',
    intensity: 'minimal',
    contextCategory: 'Temporal / Pace',
    activeVariables: { timeOfDay: 'late_night' },
  },
  {
    piece: 'heart',
    label: 'Glowing heart',
    intensity: 'full',
    contextCategory: 'Emotional / Relational',
    activeVariables: { baselineStress: 'high', socialSupport: 'isolated' },
  },
  {
    piece: 'brain',
    label: 'Simple brain',
    intensity: 'minimal',
    contextCategory: 'Literacy / Explanation',
    activeVariables: { healthKnowledge: 'basic' },
  },
  {
    piece: 'hat',
    label: 'Gentle hat',
    intensity: 'minimal',
    contextCategory: 'Authority / Credentialing',
    activeVariables: { aiComfort: 'cautious' },
  },
  {
    piece: 'dress',
    label: 'Warm wrap',
    intensity: 'moderate',
    contextCategory: 'Presentation / Identity',
    activeVariables: { motivation: 'desperate', recentLifeEvent: 'none' },
  },
  {
    piece: 'accessories',
    label: 'Comfort accessories',
    intensity: 'minimal',
    contextCategory: 'Supplementary / Modifiers',
    activeVariables: { caregiverBurden: 'none' },
  },
];

const nightNurseContext: ContextLayer = {
  timeOfDay: 'late_night',
  cyclePhase: 'not_applicable',
  season: 'winter',
  timeSinceLastInteraction: 'first_visit',
  baselineStress: 'high',
  recentLifeEvent: 'none',
  motivation: 'desperate',
  partnerStatus: 'single',
  caregiverBurden: 'none',
  socialSupport: 'isolated',
  budgetSensitivity: 'moderate',
  insuranceContext: 'insured',
  healthKnowledge: 'basic',
  supplementExperience: 'none',
  aiComfort: 'cautious',
  journeyStage: 'first_visit',
  adherenceLevel: 'unknown',
};

// --- 2. The Evidence Queen ---

const evidenceQueenGarments: GarmentState[] = [
  {
    piece: 'shoes',
    label: 'Research shoes',
    intensity: 'full',
    contextCategory: 'Temporal / Pace',
    activeVariables: { timeOfDay: 'morning' },
  },
  {
    piece: 'heart',
    label: 'Armored heart',
    intensity: 'minimal',
    contextCategory: 'Emotional / Relational',
    activeVariables: { baselineStress: 'moderate', socialSupport: 'strong' },
  },
  {
    piece: 'brain',
    label: 'Citation brain',
    intensity: 'max',
    contextCategory: 'Literacy / Explanation',
    activeVariables: { healthKnowledge: 'expert', supplementExperience: 'extensive' },
  },
  {
    piece: 'hat',
    label: 'Professional cap',
    intensity: 'full',
    contextCategory: 'Authority / Credentialing',
    activeVariables: { aiComfort: 'distrustful' },
  },
  {
    piece: 'dress',
    label: 'Full ensemble',
    intensity: 'full',
    contextCategory: 'Presentation / Identity',
    activeVariables: { motivation: 'skeptical' },
  },
  {
    piece: 'accessories',
    label: 'Evidence badges',
    intensity: 'full',
    contextCategory: 'Supplementary / Modifiers',
    activeVariables: { journeyStage: 'on_protocol' },
  },
];

const evidenceQueenContext: ContextLayer = {
  timeOfDay: 'morning',
  cyclePhase: 'not_applicable',
  season: 'fall',
  timeSinceLastInteraction: 'weeks',
  baselineStress: 'moderate',
  recentLifeEvent: 'none',
  motivation: 'skeptical',
  partnerStatus: 'partnered',
  caregiverBurden: 'none',
  socialSupport: 'strong',
  budgetSensitivity: 'low',
  insuranceContext: 'insured',
  healthKnowledge: 'expert',
  supplementExperience: 'extensive',
  aiComfort: 'distrustful',
  journeyStage: 'on_protocol',
  adherenceLevel: 'high',
};

// --- 3. The Budget Ally ---

const budgetAllyGarments: GarmentState[] = [
  {
    piece: 'shoes',
    label: 'Practical shoes',
    intensity: 'moderate',
    contextCategory: 'Temporal / Pace',
    activeVariables: { timeOfDay: 'afternoon' },
  },
  {
    piece: 'heart',
    label: 'Warm heart',
    intensity: 'moderate',
    contextCategory: 'Emotional / Relational',
    activeVariables: { baselineStress: 'moderate', socialSupport: 'moderate' },
  },
  {
    piece: 'brain',
    label: 'Clear brain',
    intensity: 'moderate',
    contextCategory: 'Literacy / Explanation',
    activeVariables: { healthKnowledge: 'basic' },
  },
  {
    piece: 'hat',
    label: 'Approachable hat',
    intensity: 'minimal',
    contextCategory: 'Authority / Credentialing',
    activeVariables: { aiComfort: 'neutral' },
  },
  {
    piece: 'dress',
    label: 'Simple wrap',
    intensity: 'minimal',
    contextCategory: 'Presentation / Identity',
    activeVariables: { budgetSensitivity: 'high', motivation: 'concerned' },
  },
  {
    piece: 'accessories',
    label: 'Cost-conscious accessories',
    intensity: 'moderate',
    contextCategory: 'Supplementary / Modifiers',
    activeVariables: { insuranceContext: 'hsa_fsa_eligible' },
  },
];

const budgetAllyContext: ContextLayer = {
  timeOfDay: 'afternoon',
  cyclePhase: 'not_applicable',
  season: 'spring',
  timeSinceLastInteraction: 'first_visit',
  baselineStress: 'moderate',
  recentLifeEvent: 'none',
  motivation: 'concerned',
  partnerStatus: 'partnered',
  caregiverBurden: 'light',
  socialSupport: 'moderate',
  budgetSensitivity: 'high',
  insuranceContext: 'hsa_fsa_eligible',
  healthKnowledge: 'basic',
  supplementExperience: 'none',
  aiComfort: 'neutral',
  journeyStage: 'first_visit',
  adherenceLevel: 'unknown',
};

// --- 4. The Comeback Coach ---

const comebackCoachGarments: GarmentState[] = [
  {
    piece: 'shoes',
    label: 'Familiar boots',
    intensity: 'moderate',
    contextCategory: 'Temporal / Pace',
    activeVariables: { timeSinceLastInteraction: 'months' },
  },
  {
    piece: 'heart',
    label: 'Steady heart',
    intensity: 'moderate',
    contextCategory: 'Emotional / Relational',
    activeVariables: { baselineStress: 'moderate', socialSupport: 'moderate' },
  },
  {
    piece: 'brain',
    label: 'Memory brain',
    intensity: 'full',
    contextCategory: 'Literacy / Explanation',
    activeVariables: { healthKnowledge: 'informed', supplementExperience: 'some' },
  },
  {
    piece: 'hat',
    label: 'Trusted hat',
    intensity: 'moderate',
    contextCategory: 'Authority / Credentialing',
    activeVariables: { aiComfort: 'comfortable' },
  },
  {
    piece: 'dress',
    label: 'Continuity outfit',
    intensity: 'moderate',
    contextCategory: 'Presentation / Identity',
    activeVariables: { journeyStage: 're_engagement', motivation: 'concerned' },
  },
  {
    piece: 'accessories',
    label: 'Progress tracker accessories',
    intensity: 'full',
    contextCategory: 'Supplementary / Modifiers',
    activeVariables: { adherenceLevel: 'moderate' },
  },
];

const comebackCoachContext: ContextLayer = {
  timeOfDay: 'morning',
  cyclePhase: 'not_applicable',
  season: 'spring',
  timeSinceLastInteraction: 'months',
  baselineStress: 'moderate',
  recentLifeEvent: 'none',
  motivation: 'concerned',
  partnerStatus: 'partnered',
  caregiverBurden: 'none',
  socialSupport: 'moderate',
  budgetSensitivity: 'moderate',
  insuranceContext: 'insured',
  healthKnowledge: 'informed',
  supplementExperience: 'some',
  aiComfort: 'comfortable',
  journeyStage: 're_engagement',
  adherenceLevel: 'moderate',
};

// --- 5. The Crisis Responder ---

const crisisResponderGarments: GarmentState[] = [
  {
    piece: 'shoes',
    label: 'Emergency shoes',
    intensity: 'full',
    contextCategory: 'Temporal / Pace',
    activeVariables: { timeOfDay: 'evening' },
  },
  {
    piece: 'heart',
    label: 'Max-glow heart',
    intensity: 'max',
    contextCategory: 'Emotional / Relational',
    activeVariables: { baselineStress: 'crisis', recentLifeEvent: 'bereavement' },
  },
  {
    piece: 'brain',
    label: 'Stripped brain',
    intensity: 'minimal',
    contextCategory: 'Literacy / Explanation',
    activeVariables: { healthKnowledge: 'basic' },
  },
  {
    piece: 'hat',
    label: 'Credential hat',
    intensity: 'full',
    contextCategory: 'Authority / Credentialing',
    activeVariables: { aiComfort: 'cautious' },
  },
  {
    piece: 'dress',
    label: 'Protective dress',
    intensity: 'full',
    contextCategory: 'Presentation / Identity',
    activeVariables: { motivation: 'desperate', socialSupport: 'isolated' },
  },
  {
    piece: 'accessories',
    label: 'Provider referral kit',
    intensity: 'max',
    contextCategory: 'Supplementary / Modifiers',
    activeVariables: { caregiverBurden: 'heavy' },
  },
];

const crisisResponderContext: ContextLayer = {
  timeOfDay: 'evening',
  cyclePhase: 'not_applicable',
  season: 'winter',
  timeSinceLastInteraction: 'days',
  baselineStress: 'crisis',
  recentLifeEvent: 'bereavement',
  motivation: 'desperate',
  partnerStatus: 'widowed',
  caregiverBurden: 'heavy',
  socialSupport: 'isolated',
  budgetSensitivity: 'moderate',
  insuranceContext: 'insured',
  healthKnowledge: 'basic',
  supplementExperience: 'none',
  aiComfort: 'cautious',
  journeyStage: 'on_protocol',
  adherenceLevel: 'low',
};

// --- 6. The Proactive Partner ---

const proactivePartnerGarments: GarmentState[] = [
  {
    piece: 'shoes',
    label: 'Smart shoes',
    intensity: 'full',
    contextCategory: 'Temporal / Pace',
    activeVariables: { timeOfDay: 'morning' },
  },
  {
    piece: 'heart',
    label: 'Balanced heart',
    intensity: 'moderate',
    contextCategory: 'Emotional / Relational',
    activeVariables: { baselineStress: 'low', socialSupport: 'strong' },
  },
  {
    piece: 'brain',
    label: 'Deep brain',
    intensity: 'max',
    contextCategory: 'Literacy / Explanation',
    activeVariables: { healthKnowledge: 'expert', supplementExperience: 'extensive' },
  },
  {
    piece: 'hat',
    label: 'Collaborative hat',
    intensity: 'full',
    contextCategory: 'Authority / Credentialing',
    activeVariables: { aiComfort: 'comfortable' },
  },
  {
    piece: 'dress',
    label: 'Premium ensemble',
    intensity: 'full',
    contextCategory: 'Presentation / Identity',
    activeVariables: { motivation: 'proactive', budgetSensitivity: 'low' },
  },
  {
    piece: 'accessories',
    label: 'Full data suite',
    intensity: 'max',
    contextCategory: 'Supplementary / Modifiers',
    activeVariables: { journeyStage: 'retesting' },
  },
];

const proactivePartnerContext: ContextLayer = {
  timeOfDay: 'morning',
  cyclePhase: 'not_applicable',
  season: 'summer',
  timeSinceLastInteraction: 'weeks',
  baselineStress: 'low',
  recentLifeEvent: 'none',
  motivation: 'proactive',
  partnerStatus: 'partnered',
  caregiverBurden: 'none',
  socialSupport: 'strong',
  budgetSensitivity: 'low',
  insuranceContext: 'insured',
  healthKnowledge: 'expert',
  supplementExperience: 'extensive',
  aiComfort: 'comfortable',
  journeyStage: 'retesting',
  adherenceLevel: 'high',
};

// --- 7. The Gentle Teacher ---

const gentleTeacherGarments: GarmentState[] = [
  {
    piece: 'shoes',
    label: 'Patient shoes',
    intensity: 'minimal',
    contextCategory: 'Temporal / Pace',
    activeVariables: { timeOfDay: 'afternoon' },
  },
  {
    piece: 'heart',
    label: 'Nurturing heart',
    intensity: 'full',
    contextCategory: 'Emotional / Relational',
    activeVariables: { baselineStress: 'moderate', socialSupport: 'moderate' },
  },
  {
    piece: 'brain',
    label: 'Analogy brain',
    intensity: 'moderate',
    contextCategory: 'Literacy / Explanation',
    activeVariables: { healthKnowledge: 'minimal', supplementExperience: 'none' },
  },
  {
    piece: 'hat',
    label: 'Mentor hat',
    intensity: 'moderate',
    contextCategory: 'Authority / Credentialing',
    activeVariables: { aiComfort: 'neutral' },
  },
  {
    piece: 'dress',
    label: 'Welcoming dress',
    intensity: 'moderate',
    contextCategory: 'Presentation / Identity',
    activeVariables: { motivation: 'curious', journeyStage: 'first_visit' },
  },
  {
    piece: 'accessories',
    label: 'Teaching aids',
    intensity: 'moderate',
    contextCategory: 'Supplementary / Modifiers',
    activeVariables: { caregiverBurden: 'none' },
  },
];

const gentleTeacherContext: ContextLayer = {
  timeOfDay: 'afternoon',
  cyclePhase: 'not_applicable',
  season: 'spring',
  timeSinceLastInteraction: 'first_visit',
  baselineStress: 'moderate',
  recentLifeEvent: 'none',
  motivation: 'curious',
  partnerStatus: 'single',
  caregiverBurden: 'none',
  socialSupport: 'moderate',
  budgetSensitivity: 'moderate',
  insuranceContext: 'insured',
  healthKnowledge: 'minimal',
  supplementExperience: 'none',
  aiComfort: 'neutral',
  journeyStage: 'first_visit',
  adherenceLevel: 'unknown',
};

// --- 8. The Cycle Companion ---

const cycleCompanionGarments: GarmentState[] = [
  {
    piece: 'shoes',
    label: 'Adaptive shoes',
    intensity: 'moderate',
    contextCategory: 'Temporal / Pace',
    activeVariables: { timeOfDay: 'evening', cyclePhase: 'luteal' },
  },
  {
    piece: 'heart',
    label: 'Cyclical heart',
    intensity: 'full',
    contextCategory: 'Emotional / Relational',
    activeVariables: { baselineStress: 'moderate', recentLifeEvent: 'none' },
  },
  {
    piece: 'brain',
    label: 'Hormonal brain',
    intensity: 'full',
    contextCategory: 'Literacy / Explanation',
    activeVariables: { healthKnowledge: 'informed', supplementExperience: 'some' },
  },
  {
    piece: 'hat',
    label: 'Specialist hat',
    intensity: 'full',
    contextCategory: 'Authority / Credentialing',
    activeVariables: { aiComfort: 'comfortable' },
  },
  {
    piece: 'dress',
    label: 'Phase-aware dress',
    intensity: 'full',
    contextCategory: 'Presentation / Identity',
    activeVariables: { cyclePhase: 'luteal', motivation: 'concerned' },
  },
  {
    piece: 'accessories',
    label: 'Cycle tracking accessories',
    intensity: 'full',
    contextCategory: 'Supplementary / Modifiers',
    activeVariables: { cyclePhase: 'luteal' },
  },
];

const cycleCompanionContext: ContextLayer = {
  timeOfDay: 'evening',
  cyclePhase: 'luteal',
  season: 'fall',
  timeSinceLastInteraction: 'weeks',
  baselineStress: 'moderate',
  recentLifeEvent: 'none',
  motivation: 'concerned',
  partnerStatus: 'partnered',
  caregiverBurden: 'none',
  socialSupport: 'strong',
  budgetSensitivity: 'moderate',
  insuranceContext: 'insured',
  healthKnowledge: 'informed',
  supplementExperience: 'some',
  aiComfort: 'comfortable',
  journeyStage: 'on_protocol',
  adherenceLevel: 'high',
};

// ============================================
// Exported Curated Personas Array
// ============================================

export const CURATED_PERSONAS: NursePersona[] = [
  // 0. The Hemologix Nurse (Dr. C Default — baseline for all first visits)
  {
    personaId: 'default_dr_c',
    name: 'The Hemologix Nurse',
    garments: defaultGarments,
    contextSnapshot: defaultContext,
    triggerConditions: 'Default — any first visit without a more specific persona match',
    conversationStyle:
      'Warm clinical confidence. Like your favorite NP who also happens to be your friend. ' +
      'She knows exactly what she is looking at. She makes you feel like you are the only patient today. ' +
      'She never sells. She explains, she listens, she earns trust before she ever mentions a supplement.',
    sampleExcerpt:
      'I see what is going on here. Your body has been telling you something, and you were right to listen. ' +
      'Let me walk you through what your labs are showing, one thing at a time.',
    isCurated: true,
    createdBy: 'system',
    usageCount: 0,
  },

  // 1. The Night Nurse
  {
    personaId: 'curated_night_nurse',
    name: 'The Night Nurse',
    garments: nightNurseGarments,
    contextSnapshot: nightNurseContext,
    triggerConditions: 'Late night + High stress + Any literacy',
    conversationStyle:
      'Empathy-first. Shortened explanations. Reduced decision load. Offers to continue tomorrow.',
    sampleExcerpt:
      "Hey \u2014 I'm really glad you're here. You're not imagining things. What you're feeling is real. We don't have to figure everything out tonight.",
    isCurated: true,
    createdBy: 'system',
    usageCount: 0,
  },

  // 2. The Evidence Queen
  {
    personaId: 'curated_evidence_queen',
    name: 'The Evidence Queen',
    garments: evidenceQueenGarments,
    contextSnapshot: evidenceQueenContext,
    triggerConditions: 'Expert literacy + Skeptical motivation + Distrustful AI comfort',
    conversationStyle:
      'Citations-forward. Evidence grades visible. Limitations acknowledged. No warmth oversell.',
    sampleExcerpt:
      'Your Vitamin D at 22 ng/mL is 56% below the functional optimal range of 50-80 ng/mL. The Endocrine Society guidelines recommend supplementation above 4,000 IU daily for values below 30.',
    isCurated: true,
    createdBy: 'system',
    usageCount: 0,
  },

  // 3. The Budget Ally
  {
    personaId: 'curated_budget_ally',
    name: 'The Budget Ally',
    garments: budgetAllyGarments,
    contextSnapshot: budgetAllyContext,
    triggerConditions: 'High/Severe budget + Any literacy + First visit or On protocol',
    conversationStyle:
      'Lowest tier first. Cost per day framing. HSA/FSA mentions. Never pushes upgrades.',
    sampleExcerpt:
      "Let's start with what makes the biggest difference for the least cost. Your Vitamin D supplement works out to about $0.33 per day.",
    isCurated: true,
    createdBy: 'system',
    usageCount: 0,
  },

  // 4. The Comeback Coach
  {
    personaId: 'curated_comeback_coach',
    name: 'The Comeback Coach',
    garments: comebackCoachGarments,
    contextSnapshot: comebackCoachContext,
    triggerConditions: 'Re-engagement or Retesting + Any stress + Moderate/High adherence',
    conversationStyle:
      'References history. Compares then vs now. Skips re-introductions. Celebrates progress.',
    sampleExcerpt:
      "Welcome back. Last time your ferritin was at 18 \u2014 let's see where you are now. I've been looking forward to this.",
    isCurated: true,
    createdBy: 'system',
    usageCount: 0,
  },

  // 5. The Crisis Responder
  {
    personaId: 'curated_crisis_responder',
    name: 'The Crisis Responder',
    garments: crisisResponderGarments,
    contextSnapshot: crisisResponderContext,
    triggerConditions: 'Crisis stress + Bereavement/New diagnosis + Any literacy',
    conversationStyle:
      'Validation only. Minimal info. Maximum warmth. Explicit offers to pause. Provider referral ready.',
    sampleExcerpt:
      "I hear you. That's a lot to carry. We can stop any time. There's no rush here.",
    isCurated: true,
    createdBy: 'system',
    usageCount: 0,
  },

  // 6. The Proactive Partner
  {
    personaId: 'curated_proactive_partner',
    name: 'The Proactive Partner',
    garments: proactivePartnerGarments,
    contextSnapshot: proactivePartnerContext,
    triggerConditions: 'Low stress + Proactive motivation + Expert literacy + Budget flexible',
    conversationStyle:
      'Full evidence depth. All tiers presented. Data density. Treats patient as peer.',
    sampleExcerpt:
      "Your inflammatory markers suggest a systemic pattern \u2014 CRP at 3.2 mg/L combined with IL-6 elevation. Let me walk you through the full protocol stack.",
    isCurated: true,
    createdBy: 'system',
    usageCount: 0,
  },

  // 7. The Gentle Teacher
  {
    personaId: 'curated_gentle_teacher',
    name: 'The Gentle Teacher',
    garments: gentleTeacherGarments,
    contextSnapshot: gentleTeacherContext,
    triggerConditions: 'Minimal/Basic literacy + None supplement experience + First visit',
    conversationStyle:
      'Explains before using terms. Uses analogies. Checks understanding. Never condescends.',
    sampleExcerpt:
      "Think of ferritin like your body's iron savings account. Right now, your account is running a bit low at 18 \u2014 ideally we'd want it closer to 50.",
    isCurated: true,
    createdBy: 'system',
    usageCount: 0,
  },

  // 8. The Cycle Companion
  {
    personaId: 'curated_cycle_companion',
    name: 'The Cycle Companion',
    garments: cycleCompanionGarments,
    contextSnapshot: cycleCompanionContext,
    triggerConditions: "Cycle phase active + Women's hormones vertical + Any stress",
    conversationStyle:
      'Symptom validation tied to phase. Protocol timing framed around cycle. Normalized language.',
    sampleExcerpt:
      "You're in your luteal phase right now \u2014 that explains why the fatigue and mood changes feel amplified. Your progesterone at 4.2 is below where we'd want it.",
    isCurated: true,
    createdBy: 'system',
    usageCount: 0,
  },
];
